import { vaultFilesApi } from "@api";
import { queriesSharedFileRequests } from "@query";
import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSDropFileInput,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSInfoBox,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  SharedFileRequest,
  VaultFileKind,
  VaultFileVisibility
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { ErrorAlert } from "@components/ErrorAlert";
import { useWSMutation } from "../../../../../query/helpers";
import { toLocalVaultUploadUrl } from "../../../../../v3/utils/toLocalVaultUploadUrl";
import { mimeTypesToGroups } from "../../../../RequirementDefinitions/modules/SharedFile/createRequirementDefinition/utils";
import { RequirementInstructionsView } from "../../../components/RequirementInstructionsView/RequirementInstructionsView";

type Props = {
  onSuccess: (fileId: string) => void;
  sharedFileRequest: SharedFileRequest;
  onClose: (result: boolean) => void;
};

/**
 * The v3 replacement for the v1 member-files multipart upload: presign
 * (`createVaultFileUpload`) → PUT the bytes at the returned storage URL →
 * register the vault file (`createVaultFile`, the `uploadId` arm added in SDK
 * 3.10.0, which replaced the previously-required pre-hosted `fileUrl`).
 *
 * Composed by hand rather than added to the registry: the registry is 1:1 with
 * the SDK, and this orchestrates two SDK calls around a byte PUT that is not an
 * SDK call at all. Returns the new vault-file id for the bind below.
 *
 * TODO(v3-blocker): the byte PUT fails its CORS preflight in a browser — the
 * vault storage bucket does not yet allow cross-origin PUT from app origins.
 * `src/v3/api/tests/specs/vaultFiles.itest.ts` covers this flow and passes, but
 * it runs under Node, which does not enforce CORS, so it cannot catch this.
 * Server-to-server the three steps all succeed.
 *
 * The fix is a CORS policy on the bucket (`gs://wingspan-integration-file-vault`
 * and its staging/production twins) — nothing here can make GCS emit
 * `Access-Control-Allow-Origin`. It must allow method `PUT` from the app origins
 * and allowlist every header the presign demands, since all three are covered by
 * `X-Goog-SignedHeaders` and cannot be dropped to avoid the preflight:
 * `Content-Type`, `x-goog-if-generation-match`, `x-goog-content-length-range`.
 * `GET`/`HEAD` want the same treatment — `getVaultFileContent` redirects to the
 * same host, so reading bytes via fetch/XHR will hit this too.
 * Locally, `toLocalVaultUploadUrl` routes the PUT through the dev-server proxy
 * in `src/setupProxy.js` so the flow can be exercised until the bucket is fixed.
 *
 * TODO(v3-verify): v1 granted the counterparty access explicitly
 * (`viewerIds: [payeeId, payerId]`). v3 has no such field — `VaultFile.viewers`
 * is read-only/derived — so access is expected to follow from binding the file
 * to the shared-file request, which already knows both parties. Confirm in QA
 * that the counterparty can open a file the other side uploaded (blocked on the
 * bind above).
 */
const useMutationUploadVaultFile = () =>
  useWSMutation<string, unknown, File>(async file => {
    const upload = await vaultFilesApi.createVaultFileUpload({
      vaultFileUploadCreate: {
        filename: file.name,
        mimeType: file.type,
        contentLength: file.size
      }
    });

    const uploadResponse = await fetch(
      toLocalVaultUploadUrl(upload.uploadUrl),
      {
        method: "PUT",
        headers: upload.requiredHeaders,
        body: file
      }
    );

    if (!uploadResponse.ok) {
      throw new Error(
        `Failed to upload file (${uploadResponse.status} ${uploadResponse.statusText})`
      );
    }

    const vaultFile = await vaultFilesApi.createVaultFile({
      vaultFileCreate: {
        uploadId: upload.uploadId,
        title: file.name,
        filename: file.name,
        mimeType: file.type,
        kind: VaultFileKind.DOCUMENT,
        visibility: VaultFileVisibility.PRIVATE
      }
    });

    if (!vaultFile.id) {
      throw new Error("Uploaded file was registered without an id");
    }

    return vaultFile.id;
  });

const UploadFileModal: React.FC<Props> = ({
  sharedFileRequest,
  onSuccess,
  onClose
}) => {
  const [file, setFile] = React.useState<File | null>(null);
  const { openSnackbar } = useWSSnackbar();

  const [uploadFile, uploadFileMeta] = useMutationUploadVaultFile();

  // TODO(v3-blocker): the bind REJECTS the vault-file id minted above —
  // `POST /v3/compliance/shared-file-requests/:id/upload` answers 422
  // `{field: "fileId", code: "InvalidFormat"}`. The two v3 endpoints disagree on
  // id format: the bind validates `fileId` as a v3-native 22-char base62 id (the
  // shape of `sharedFileRequest.id` itself, e.g. `agvu5zluISLj4pu26G2DFV`),
  // while `createVaultFile` mints a 24-char hex Mongo ObjectId (e.g.
  // `6d80c8238d522c99939bab7e`). So no id this flow can produce is acceptable to
  // the bind — BE has to reconcile the two. Verified in
  // `src/v3/api/tests/specs/vaultFiles.itest.ts`, which is deliberately RED on
  // this and prints both id shapes; every other test in that suite passes.
  // Cache invalidation is declared on the registry entry itself.
  const [uploadSharedFileRequestFile, uploadSharedFileRequestFileMeta] =
    queriesSharedFileRequests.uploadSharedFileRequestFile.useMutation();

  const acceptedMimeTypes = sharedFileRequest.acceptedMimeTypes ?? [];
  const formatsList = mimeTypesToGroups(acceptedMimeTypes).join(", ");

  return (
    <WSList gap="L">
      {sharedFileRequest.instructions ? (
        <WSInfoBox>
          <RequirementInstructionsView
            instructions={sharedFileRequest.instructions}
          />
        </WSInfoBox>
      ) : null}

      {file ? (
        <WSPanel p="XL">
          <WSFlexBox.CenterY wrap="nowrap">
            <WSText.Paragraph singleLine>{file.name}</WSText.Paragraph>
            <WSFlexBox>
              <WSIcon
                block
                color="gray500"
                mx="M"
                name="exit"
                size="M"
                onClick={() => {
                  setFile(null);
                }}
              />
            </WSFlexBox>
          </WSFlexBox.CenterY>
        </WSPanel>
      ) : (
        <WSElement>
          <WSText.ParagraphSm color="gray500" mb="S">
            Only {formatsList} files are accepted.
          </WSText.ParagraphSm>
          <WSDropFileInput
            accept={acceptedMimeTypes}
            buttonProps={{
              type: "button",
              icon: "upload"
            }}
            buttonText="Browse file"
            maxSize={1000000 * 100}
            name="dropFileInput"
            onDropAccepted={accepted => {
              setFile(accepted[0]);
            }}
          />
        </WSElement>
      )}

      <ErrorAlert
        context="Other"
        error={uploadSharedFileRequestFileMeta.error || uploadFileMeta.error}
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            kind: "Tertiary",
            label: "Cancel",
            onClick: () => {
              onClose(false);
            }
          },
          {
            kind: "Primary",
            label: "Continue with upload",
            disabled: !file,
            onAsyncClick: async () => {
              const fileId = await uploadFile(file!, {
                onError: () => {
                  openSnackbar({
                    message: "Failed to upload file",
                    type: "error"
                  });
                },
                throwOnError: true
              });

              if (fileId) {
                const updatedSharedFile = await uploadSharedFileRequestFile(
                  {
                    sharedFileRequestId: sharedFileRequest.id!,
                    sharedFileRequestUpload: { fileId }
                  },
                  {
                    onError: () => {
                      openSnackbar({
                        message: "Failed to update shared file",
                        type: "error"
                      });
                    },
                    throwOnError: true
                  }
                );

                if (updatedSharedFile) {
                  // requirement/engagement cache invalidation is declared on
                  // the registry entry's own `dependencies`
                  openSnackbar({
                    message: "File uploaded successfully",
                    type: "success"
                  });

                  onSuccess(sharedFileRequest.id!);
                  onClose(true);

                  return;
                }
              }
            }
          }
        ]}
        orientation="horizontal"
      />
    </WSList>
  );
};

export function useUploadFileModal() {
  return useWSModal(UploadFileModal, {
    title: "Upload file",
    size: "M"
  });
}
